import React, { useState } from 'react';
import { motion, AnimatePresence, useDragControls } from 'motion/react';
import { formatCurrencyForDisplay } from '@/lib/utils/currencyFormat';
import type { EventBookingCategoryResponse } from '@/features/booking/types/eventBookingCategoryTypes';
import { MOBILE_POPUP_HEIGHT } from '@/features/booking/constants/mobileBooking';

interface PriceBreakdown {
  totalPrice: number;
  perGuestBreakdown: Array<{
    addOns: Array<{
      addOn: {
        add_on: {
          name: string;
        };
      };
      price: number;
    }>;
  }>;
}

interface MobileTicketFloatingSummaryProps {
  priceBreakdown: PriceBreakdown;
  guestCount: number;
  onGuestCountChange: (count: number) => void;
  eventBookingCategory: EventBookingCategoryResponse | null;
  onAddToCart: () => void;
  addingToCart: boolean;
  buttonText: string;
  isUpdateMode: boolean;
  onUpdateCart?: () => void;
  cartId: string | null;
  addOnCount: number;
  isLoadingCarts: boolean;
  hasCartChanges: boolean;
  hasViewCartButton: boolean;
  hasCartItemForEBC: boolean;
  currency?: string;
  onViewCart: () => void;
}

const MobileTicketFloatingSummary: React.FC<MobileTicketFloatingSummaryProps> = ({
  priceBreakdown,
  guestCount,
  onGuestCountChange,
  eventBookingCategory,
  onAddToCart,
  addingToCart,
  buttonText,
  isUpdateMode,
  onUpdateCart,
  addOnCount,
  isLoadingCarts,
  hasCartChanges,
  hasViewCartButton,
  hasCartItemForEBC,
  currency = 'USD',
  onViewCart
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const dragControls = useDragControls();
  const formatPrice = (amount: number) => formatCurrencyForDisplay(amount, currency);
  
  // Calculate if the button should be disabled
  // Disable if loading, adding, or if we're in update mode but nothing changed (UNLESS guest count is 0 and it's already in the cart, which triggers delete)
  const isButtonDisabled = isLoadingCarts || addingToCart || (isUpdateMode && !hasCartChanges && !(hasCartItemForEBC && guestCount === 0));
  
  const basePrice = eventBookingCategory?.price || 0;

  return (
    <>
      {/* Backdrop for expanded state */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsExpanded(false)}
            className="fixed inset-0 bg-black/60 z-[45]"
            style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
          />
        )}
      </AnimatePresence>

      {/* Sticky Bottom Bar Container */}
      <motion.div
        className="fixed bottom-0 left-0 right-0 z-[55] flex flex-col justify-end pointer-events-auto"
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        {/* Expanded Content (Drawer) positioned behind the bar */}
        <div className="relative -mb-6 pt-0 -z-10 w-full flex flex-col">
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: MOBILE_POPUP_HEIGHT, opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="flex flex-col pt-3 relative rounded-t-[32px] bg-[#0a0a0a] border-t border-white/10 shadow-[0_-10px_40px_rgba(0,0,0,0.5)] overflow-hidden pointer-events-auto"
                style={{ 
                  height: `calc(${MOBILE_POPUP_HEIGHT} + 120px)`, 
                  paddingBottom: '120px' 
                }}
                drag="y"
                dragControls={dragControls}
                dragListener={false}
                dragConstraints={{ top: 0, bottom: 300 }}
                dragElastic={0.3}
                onDragEnd={(_, info) => {
                  if (info.offset.y > 80 || info.velocity.y > 400) {
                    setIsExpanded(false);
                  }
                }}
              >
                {/* Fixed Top Header — drag handle */}
                <div 
                  className="bg-[#0a0a0a] px-6 pt-5 pb-2 z-20 shrink-0 border-b border-white/5 relative shadow-sm flex flex-col items-center cursor-grab active:cursor-grabbing touch-none"
                  onPointerDown={(e) => dragControls.start(e)}
                >
                  {/* Slider Handle */}
                  <div className="top-3 w-12 h-1.5 rounded-full pointer-events-none transition-colors duration-300 bg-gray-600 absolute" />
                  <h3 className="text-white font-bold text-xl mt-2 self-start">Summary</h3>
                </div>

                {/* Scrollable Content */}
              <div 
                className="px-6 py-4 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800 flex-1"
                style={{ maxHeight: MOBILE_POPUP_HEIGHT }}
                onPointerDown={(e) => e.stopPropagation()}
              >
                  
                  <div className="space-y-4">
                    {/* Guest Count */}
                    <div className="flex justify-between items-center py-2 border-b border-gray-700/50 text-sm">
                      <span className="text-gray-400">Total Guests</span>
                      <span className="text-white font-medium">{guestCount}</span>
                    </div>

                    {/* Ticket Base Price Info */}
                    {(guestCount > 0) && (
                      <>
                        <div className="flex justify-between items-center py-2 border-b border-gray-700/50 text-sm">
                          <span className="text-gray-400">Ticket Price</span>
                          <span className="text-white font-medium">
                            {formatPrice(basePrice)}
                          </span>
                        </div>
                        
                        <div className="flex justify-between items-center py-2 border-b border-gray-700/50 text-sm">
                          <span className="text-white">Tickets Base ({guestCount} × {formatPrice(basePrice)})</span>
                          <span className="text-white font-medium">{formatPrice(basePrice * guestCount)}</span>
                        </div>
                      </>
                    )}

                    {/* Add-ons */}
                    {priceBreakdown?.perGuestBreakdown?.map((guest, guestIndex) => {
                      const aggregatedAddOns = guest.addOns.reduce((acc, addOn) => {
                        const name = addOn.addOn.add_on.name;
                        if (!acc[name]) {
                          acc[name] = { count: 0, unitPrice: addOn.price, totalPrice: 0 };
                        }
                        acc[name].count++;
                        acc[name].totalPrice += addOn.price;
                        return acc;
                      }, {} as Record<string, { count: number; unitPrice: number; totalPrice: number }>);
                      
                      const addOnEntries = Object.entries(aggregatedAddOns);
                      
                      return addOnEntries.length > 0 ? (
                        <div key={guestIndex} className="py-2 border-b border-gray-700/50">
                          <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Guest {guestIndex + 1} Add-ons</div>
                          {addOnEntries.map(([name, { count, totalPrice }], addOnIndex) => (
                            <div key={addOnIndex} className="flex justify-between items-center text-sm mb-1.5 last:mb-0">
                              <span className="text-gray-300">
                                {name}{count > 1 && <span className="text-blue-400 ml-1">×{count}</span>}
                              </span>
                              <span className="text-white">{formatPrice(totalPrice)}</span>
                            </div>
                          ))}
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>

                {/* Fixed Total */}
                <div className="px-6 pt-4 pb-8">
                  {/* Total row at bottom of scroll area */}
                  <div className="flex justify-between items-center pt-4 mt-2 border-t border-white/10 px-1">
                    <span className="text-white font-bold text-lg">Total</span>
                    <span className="text-blue-400 font-black text-2xl">
                      {formatPrice(priceBreakdown.totalPrice)}
                    </span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Unified Bottom Section */}
        <div className="bg-[#0a0a0a] z-10 w-full flex flex-col border-t border-white/5 relative pointer-events-auto">
          {/* Persistent Bottom Bar - Hidden if no money, unless we need to show the Remove Item button */}
          {(priceBreakdown.totalPrice > 0 || (hasCartItemForEBC && guestCount === 0)) && (
            <div className="px-5 pt-3 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] flex items-center justify-between gap-4 relative">
              
              {/* Left: Price and Expand Toggle */}
            <div 
              className="flex-1 flex flex-col justify-center cursor-pointer min-w-0"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <div className="flex items-center gap-2">
               <span className="text-xl font-bold text-white truncate">
                {formatPrice(priceBreakdown.totalPrice)}
              </span>
              <svg 
                className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            </div>
            
            <div className="text-xs text-gray-400 truncate mt-0.5 font-medium">
              {guestCount} {guestCount === 1 ? 'guest' : 'guests'} • {addOnCount} {addOnCount === 1 ? 'addon' : 'addons'}
            </div>
          </div>

          {/* Right: Actions */}
          <div className={`flex gap-2 shrink-0 ${!hasViewCartButton ? 'flex-1' : ''}`}>
            {hasViewCartButton && (
              <button
                onClick={onViewCart}
                className="px-5 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm shadow-[0_0_20px_rgba(37,99,235,0.4)] transition-all active:scale-95 border-transparent"
              >
                View Cart
              </button>
            )}

            <div className={`relative flex items-stretch ${!hasViewCartButton ? 'flex-1' : ''}`}>
              {/* Floating Guest Selector - Attached to Action Button */}
              <div className="absolute bottom-[calc(100%+20px)] right-0 w-[160px] z-20">
                <div className="bg-[#0a0a0a] shadow-[0_-5px_15px_rgba(0,0,0,0.5)] rounded-xl flex items-center justify-between border border-white/5 pr-1 pl-3 py-1 w-full box-border">
                  <span className="text-gray-400 font-bold text-[10px] uppercase tracking-wider">guests</span>
                  <div className="flex items-center gap-3">
                    <motion.button
                      whileTap={{ scale: 0.8, backgroundColor: 'rgba(255,255,255,0.1)' }}
                      transition={{ type: 'spring', stiffness: 500, damping: 20 }}
                      onClick={() => {
                        const minCount = hasCartItemForEBC ? 0 : 1;
                        onGuestCountChange(Math.max(minCount, guestCount - 1));
                      }}
                      disabled={guestCount <= (hasCartItemForEBC ? 0 : 1) || isLoadingCarts}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 disabled:opacity-30 rounded-lg"
                    >
                      <svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1H11" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                      </svg>
                    </motion.button>
                    
                    <span className="text-sm font-bold text-white w-5 text-center">{guestCount}</span>
                    
                    <motion.button
                      whileTap={{ scale: 0.8, backgroundColor: 'rgba(255,255,255,0.1)' }}
                      transition={{ type: 'spring', stiffness: 500, damping: 20 }}
                      onClick={() => onGuestCountChange(guestCount + 1)}
                      disabled={isLoadingCarts}
                      className="w-8 h-8 flex items-center justify-center text-white rounded-lg"
                    >
                       <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 6H11M6 1V11" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                      </svg>
                    </motion.button>
                  </div>
                </div>
              </div>

              <button
                onClick={() => {
                  if (isUpdateMode && onUpdateCart) {
                    onUpdateCart();
                  } else {
                    onAddToCart();
                  }
                  setIsExpanded(false);
                }}
                disabled={isButtonDisabled}
                className={`w-full px-5 py-3 rounded-xl font-bold text-sm shadow-lg transition-all border outline-none overflow-hidden relative active:scale-95 flex items-center justify-center ${
                   isButtonDisabled
                    ? 'bg-gray-800 text-gray-500 cursor-not-allowed border-transparent'
                  : hasCartItemForEBC && guestCount === 0
                  ? 'bg-red-600 hover:bg-red-500 text-white border-transparent shadow-[0_0_15px_rgba(220,38,38,0.4)]'
                  : hasCartItemForEBC && !hasCartChanges
                  ? 'bg-transparent text-gray-400 border-gray-800 cursor-not-allowed'
                  : hasCartItemForEBC && hasCartChanges
                  ? 'bg-amber-400 hover:bg-amber-300 text-black border-transparent shadow-[0_0_15px_rgba(251,191,36,0.2)]'
                  : 'bg-blue-600 hover:bg-blue-500 text-white border-transparent shadow-[0_0_15px_rgba(37,99,235,0.4)]'
              }`}
            >
              {addingToCart ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-current rounded-full border-t-transparent animate-spin" />
                  <span>Adding...</span>
                </div>
              ) : hasCartItemForEBC && !hasCartChanges ? (
                <div className="flex items-center justify-center gap-1.5 opacity-90">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>In Cart</span>
                </div>
              ) : (
                <div className={`flex items-center justify-center gap-1.5 opacity-90 font-semibold ${hasCartItemForEBC && hasCartChanges && guestCount > 0 ? 'text-black' : 'text-white'}`}>
                  <span>{hasCartItemForEBC && guestCount === 0 ? 'Remove Item' : buttonText}</span>
                </div>
              )}
            </button>
          </div>
          </div>

          </div>
          )}
        </div>
      </motion.div>
    </>
  );
};

export default MobileTicketFloatingSummary;
